import * as Dialog from '@radix-ui/react-dialog';
import { cn } from './cn';
import Icon from './Icon';

// Dialog that presents as a bottom sheet on phones and a centred panel from the
// tablet breakpoint up. Replaces Modal.jsx, which had no focus trap, no scroll
// lock, no Escape handling and no labelled dialog role.
//
// Radix handles all of that: focus is trapped while open and restored to the
// trigger on close, the rest of the page is inert to screen readers, background
// scrolling is locked, and Escape / overlay click close it.

export default function Sheet({
  open,
  onOpenChange,
  title,
  description,
  footer,
  size = 'md',
  children,
}) {
  const width = size === 'lg' ? 'sm:max-w-[720px]' : size === 'sm' ? 'sm:max-w-[420px]' : 'sm:max-w-[560px]';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-[150] bg-[rgba(14,23,38,0.45)] backdrop-blur-[2px]',
            'data-[state=open]:animate-[authFadeIn_160ms_ease-out]',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed z-[160] flex flex-col bg-surface shadow-e3 focus:outline-none',
            // phone: bottom sheet
            'inset-x-0 bottom-0 max-h-[88dvh] rounded-t-xl border-t border-line',
            'data-[state=open]:animate-[sheet-up_240ms_cubic-bezier(0.16,1,0.3,1)]',
            // tablet+: centred panel
            'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100%-32px)]',
            'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border',
            'sm:data-[state=open]:animate-[sweep-in_200ms_cubic-bezier(0.16,1,0.3,1)]',
            width,
          )}
        >
          {/* Grab handle — phone only, purely decorative */}
          <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-pill bg-surface-4 sm:hidden" aria-hidden="true" />

          <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
            <div className="min-w-0">
              <Dialog.Title className="font-heading text-[21px] font-bold leading-tight text-ink">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-0.5 text-[14.5px] text-ink-3">{description}</Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-md text-ink-3',
                'transition-colors duration-150 hover:bg-surface-3 hover:text-ink',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
              )}
              aria-label="Sluiten"
            >
              <Icon name="close" size={18} />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-[max(20px,env(safe-area-inset-bottom))]">{children}</div>

          {footer && (
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
